import { useState } from "react";
import { Train, Bus, MapPin, ExternalLink, Info } from "lucide-react";

const BusTrain = () => {
  const providers = [
    {
      name: "Omio",
      url: "https://www.omio.com",
      description: "Trains, buses & flights across Europe",
      iconBg: "bg-gradient-to-br from-sky-500 to-cyan-600",
      icon: Train,
    },
    {
      name: "Rome2rio",
      url: "https://www.rome2rio.com",
      description: "Discover how to get anywhere",
      iconBg: "bg-gradient-to-br from-emerald-500 to-teal-600",
      icon: MapPin,
    },
    {
      name: "Trainline",
      url: "https://www.thetrainline.com",
      description: "European train tickets",
      iconBg: "bg-gradient-to-br from-indigo-500 to-blue-600",
      icon: Train,
    },
    {
      name: "FlixBus",
      url: "https://www.flixbus.com",
      description: "Budget bus travel",
      iconBg: "bg-gradient-to-br from-lime-500 to-green-600",
      icon: Bus,
    },
    {
      name: "12Go",
      url: "https://12go.asia",
      description: "Asia transport booking",
      iconBg: "bg-gradient-to-br from-orange-500 to-amber-600",
      icon: Train,
    },
    {
      name: "Greyhound",
      url: "https://www.greyhound.com",
      description: "Bus travel across USA",
      iconBg: "bg-gradient-to-br from-blue-500 to-indigo-600",
      icon: Bus,
    },
    {
      name: "Amtrak",
      url: "https://www.amtrak.com",
      description: "Train travel across USA",
      iconBg: "bg-gradient-to-br from-red-500 to-rose-600",
      icon: Train,
    },
    {
      name: "Eurostar",
      url: "https://www.eurostar.com",
      description: "High-speed UK to Europe",
      iconBg: "bg-gradient-to-br from-yellow-500 to-amber-600",
      icon: Train,
    },
  ];

  const regionalNotes = [
    { region: "Europe", note: "Trainline and Omio have the best coverage for trains" },
    { region: "Southeast Asia", note: "12Go recommended for buses, trains, and ferries" },
    { region: "India", note: "IRCTC (irctc.co.in) is the official train booking site" },
    { region: "Japan", note: "JR Pass for unlimited travel, book at jrpass.com" },
    { region: "USA", note: "Amtrak for trains, Greyhound/FlixBus for buses" },
  ];

  return (
    <div className="page-container" data-testid="bus-train-page">
      <div className="pb-28">
        {/* Hero Banner */}
        <div className="relative h-36 sm:h-44 overflow-hidden mb-6">
          <img src="https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 pb-5">
            <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">Bus & Train</h1>
            <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Ground transportation options</p>
          </div>
        </div>
        <div className="px-4">

        {/* Provider Cards */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Transport Booking Sites</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {providers.map((provider, idx) => {
              const Icon = provider.icon;
              return (
                <a
                  key={idx}
                  href={provider.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-card p-4 hover:scale-[1.02] transition-all group border-2 border-transparent hover:border-white/20"
                  data-testid={`provider-${provider.name.toLowerCase()}`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-14 h-14 rounded-xl ${provider.iconBg} flex items-center justify-center shadow-lg`}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-white text-lg">{provider.name}</h3>
                        <ExternalLink className="w-4 h-4 text-white/60 group-hover:text-white transition-colors" />
                      </div>
                      <p className="text-sm text-white/70">{provider.description}</p>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        {/* Regional Notes */}
        <div className="glass-card p-4">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-emerald-400" />
            Regional Coverage Notes
          </h3>
          <div className="space-y-3">
            {regionalNotes.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <span className="text-xs font-medium text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded whitespace-nowrap">
                  {item.region}
                </span>
                <p className="text-sm text-zinc-400 flex-1">{item.note}</p>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default BusTrain;
